import {
  createColumnHelper,
  getCoreRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronRight, Pencil, Search, Trash2, UserPlusIcon, WandIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import RequestAQuoteButton from 'components/partners/RequestAQuoteButton'
import { type MetadataConfiguration } from 'components/quoting/types'
import { adminProspects, adminQuotingSmartCensusParseJobs, partnersProspects, partnersQuoteRequest } from 'rails_routes'
import { Badge, Button, ButtonGroup, ButtonLink, IconButton, IconLink, Modal, TextButton, TextInput } from 'slab'
import { useDeleteQuotingProspectsId, useGetQuotingProspects } from 'utils/web_api'
import { type QuotingProspect, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { CreateOrEditProspect } from './CreateOrEditProspect'
import { ProspectOwnerLink } from './ProspectOwnerLink'
import { getLocaleDateString } from '../../utils'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<QuotingProspect>()

// Define getColumns function
const getColumns = (
  isForAdminView: boolean,
  showEIN: boolean,
  quotingV2EditProspectPath?: string,
  metadataConfiguration?: MetadataConfiguration,
  onDelete: (prospect: QuotingProspect) => void = () => {}) => [
  columnHelper.accessor('name', {
    header: 'Name',
    id: 'name',
    cell: ({ row }: { row: Row<QuotingProspect> }) => {
      if (row.original.name) {
        if (isForAdminView && row.original.quote_request_status !== QuotingProspectQuoteRequestStatus.complete) {
          return (
            <div className='flex items-center gap-1'>
              <span className='flex-grow'>{row.original.name}</span>
              <Badge type='neutral'>Draft</Badge>
            </div>
          )
        } else {
          return row.original.name
        }
      } else {
        return <span className='italic text-gray-400'>(no name)</span>
      }
    }
  }),
  columnHelper.accessor(
    (row) => ({
      content: <ProspectOwnerLink prospect={row} />,
    }),
    {
      header: 'Owner',
      id: 'owner',
      cell: (info) => info.getValue().content,
    }
  ),
  // This column is for broker admins. Thatch admins will be shown the <ProspectOwnerLink /> component
  columnHelper.accessor('owner_email', {
    header: 'Owner',
  }),
  columnHelper.accessor('email', {
    header: 'Email',
  }),
  columnHelper.accessor('created_at', {
    header: 'Date created',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue()))
        : undefined,
  }),
  columnHelper.accessor('ein', {
    header: 'EIN',
  }),
  {
    accessorKey: 'actions',
    header: '',
    cell: ({ row }: { row: Row<QuotingProspect> }) => (
      <div className='flex gap-4 items-center justify-end whitespace-nowrap'>
        {row.original.quote_request_status === QuotingProspectQuoteRequestStatus.complete
          ? (
              quotingV2EditProspectPath
                ? (
                  <IconLink
                    href={quotingV2EditProspectPath?.replace(':id', row.original.id) || ''}
                    label='Edit prospect'
                    icon={Pencil}
                  />)
                : (
                  <CreateOrEditProspect
                    prospect={row.original}
                    isCreate={false}
                    isForAdminView={isForAdminView}
                    showEIN={showEIN || isForAdminView}
                    triggerChildren={
                      <IconButton
                        label='Edit prospect'
                        title='Edit prospect'
                        aria-label='Edit prospect'
                        icon={Pencil}
                      />
                    }
                    metadataConfiguration={metadataConfiguration}
                  />
                  ))
          : (
            <IconButton
              icon={Trash2}
              label='Delete prospect'
              variant='destructive'
              onClick={(event) => {
                event.stopPropagation()

                onDelete(row.original)
              }}
            />
            )}
        <ChevronRight size={16} className='stroke-gray-500' />
      </div>
    ),
  },
]

type ProspectsListProps = {
  currentPartnerId?: string;
  isForAdminView?: boolean;
  pageParam?: number | null;
  showEIN?: boolean;
  showOwnerEmail?: boolean;
  quotingV2Enabled?: boolean;
  quotingV2NewProspectPath?: string;
  quotingV2EditProspectPath?: string;
  metadataConfiguration?: MetadataConfiguration;
  canAccessCade?: boolean;
  useMultiStepQuoteRequestFlow?: boolean
}

export function ProspectsListImpl ({
  currentPartnerId,
  isForAdminView = false,
  pageParam,
  showEIN = false,
  showOwnerEmail = false,
  quotingV2Enabled = false,
  quotingV2NewProspectPath,
  quotingV2EditProspectPath,
  metadataConfiguration,
  canAccessCade = false,
  useMultiStepQuoteRequestFlow = false,
}: ProspectsListProps) {
  const { assign } = useWindowLocation()

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [prospectToDelete, setProspectToDelete] = useState<QuotingProspect>()
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Get columns by calling the function, adjusting the passed prop
  const columns = React.useMemo(() => {
    return getColumns(isForAdminView, showEIN || isForAdminView, quotingV2EditProspectPath, metadataConfiguration, (prospect) => {
      setProspectToDelete(prospect)
      setDeleteModalOpen(true)
    })
  }, [showEIN, isForAdminView, quotingV2EditProspectPath, metadataConfiguration])

  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300)

  const { data, isLoading, isError, refetch } = useGetQuotingProspects({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    'filter[name]': debouncedSearchQuery,
    partner_id: (isForAdminView || !currentPartnerId) ? undefined : currentPartnerId,
  })

  const { mutate: deleteProspect } = useDeleteQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        setDeleteModalOpen(false)
        refetch()
      },
      onError: () => {
        toast.error('Failed to delete draft prospect')
      }
    }
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        name: true,
        owner: isForAdminView,
        owner_email: showOwnerEmail && !isForAdminView,
        email: true,
        ein: showEIN || isForAdminView,
        created_at: true,
        actions: true,
      },
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  useEffect(() => {
    const pageURL = new URL(window.location.href)
    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)
    window.history.pushState({}, '', pageURL.toString())
  }, [pagination.pageIndex])

  const deleteCurrentProspect = () => prospectToDelete && deleteProspect({ id: prospectToDelete.id })

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='heading-lg'>Prospects</h1>
        <>
          <div className='flex items-start gap-2'>
            {canAccessCade && (
              <ButtonLink
                href={adminQuotingSmartCensusParseJobs.index()}
                variant='secondary'
                leftIcon={WandIcon}
              >
                CADE
              </ButtonLink>
            )}
            {quotingV2NewProspectPath && quotingV2Enabled
              ? (
                <div className='flex flex-col gap-3 items-end'>
                  <ButtonLink
                    href={quotingV2NewProspectPath}
                    variant='primary'
                    leftIcon={UserPlusIcon}
                  >
                    Create new prospect
                  </ButtonLink>
                  <CreateOrEditProspect
                    isForAdminView={isForAdminView}
                    isCreate
                    showEIN={showEIN || isForAdminView}
                    metadataConfiguration={metadataConfiguration}
                    triggerChildren={
                      <TextButton color='purple'>
                        use old tool
                      </TextButton>
              }
                  />
                </div>
                )
              : (
                <RequestAQuoteButton
                  location='top_nav'
                  useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
                  canSelfServeQuotes={quotingV2Enabled}
                />
                )}
          </div>
        </>
      </div>
      <div className='w-full mb-4 sm:w-1/2 sm:max-w-sm'>
        <TextInput
          id='search-prospects'
          placeholder='Find a prospect'
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value)
            setPagination({ ...pagination, pageIndex: 1 })
          }}
          leadingIcon={Search}
          className='input-default'
        />
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='There are no prospects to display.'
        isThisRowClicklable={() => true}
        onRowClick={({ event, row }) => {
          // prevent going to the details page when the user is interacting with the menu item or the dialog
          let currentElement: HTMLElement | null = event.target as HTMLElement
          let menuTriggered = false

          while (currentElement && currentElement !== event.currentTarget) {
            if (
              currentElement.getAttribute('aria-label') === 'Edit prospect' ||
              currentElement.getAttribute('role') === 'dialog' ||
              !!currentElement.getAttribute('data-state') || // for the overlay of the dialog
              currentElement.getAttribute('aria-label') ===
                'Owner inspect link' ||
              currentElement.getAttribute('aria-label') === 'External link'
            ) {
              menuTriggered = true
              break
            }
            currentElement = currentElement.parentElement
          }

          if (!menuTriggered) {
            const recordId = row.original.id
            if (isForAdminView) {
              assign(adminProspects.show({ id: recordId }))
            } else if (row.original.quote_request_status === QuotingProspectQuoteRequestStatus.complete) {
              assign(partnersProspects.show({ id: recordId }))
            } else {
              assign(partnersQuoteRequest.show({ id: recordId }))
            }
          } else {
            event.stopPropagation()
          }
        }}
        paginationLabelSingular='prospect'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
      <Modal open={isDeleteModalOpen} onOpenChange={setDeleteModalOpen} aria-describedby={undefined}>
        <Modal.Header>
          <Modal.Title>Delete {prospectToDelete?.name ?? 'prospect'}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This action can't be undone. All progress associated with {prospectToDelete?.name ?? 'this prospect'}, including information entered and files uploaded, will be deleted.</p>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup align='right'>
            <Button
              variant='secondary'
              size='large'
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='primary-destructive'
              size='large'
              leftIcon={Trash2}
              onClick={deleteCurrentProspect}
            >
              Delete
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

const ProspectsList = ({
  currentPartnerId,
  isForAdminView,
  pageParam,
  showEIN,
  showOwnerEmail,
  quotingV2Enabled,
  quotingV2NewProspectPath,
  quotingV2EditProspectPath,
  metadataConfiguration,
  canAccessCade,
  useMultiStepQuoteRequestFlow,
}: ProspectsListProps) => {
  return (
    <ProspectsListImpl
      currentPartnerId={currentPartnerId}
      isForAdminView={isForAdminView}
      pageParam={pageParam}
      showEIN={showEIN}
      showOwnerEmail={showOwnerEmail}
      quotingV2Enabled={quotingV2Enabled}
      quotingV2NewProspectPath={quotingV2NewProspectPath}
      quotingV2EditProspectPath={quotingV2EditProspectPath}
      metadataConfiguration={metadataConfiguration}
      canAccessCade={canAccessCade}
      useMultiStepQuoteRequestFlow={useMultiStepQuoteRequestFlow}
    />
  )
}

export default ProspectsList
